import { useMemo, useState } from 'react'
import { backupDue, compareText, filterByQuery, parseChordPro } from '../../domain'
import { getDb } from '../../storage/db'
import { newSongDocument } from '../../storage/factory'
import { createSong, getMeta, listSongs, type SongListItem } from '../../storage/repository'
import common from '../common.module.css'
import { useLive } from '../hooks/useLive'
import { navigate, paths } from '../router'
import { t } from '../strings'
import styles from './Repertoire.module.css'

type Sort = 'title' | 'artist' | 'recent'

function sortSongs(songs: SongListItem[], sort: Sort): SongListItem[] {
  const copy = [...songs]
  if (sort === 'recent') return copy.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
  if (sort === 'artist') {
    return copy.sort((a, b) => compareText(a.artist, b.artist) || compareText(a.title, b.title))
  }
  return copy.sort((a, b) => compareText(a.title, b.title) || compareText(a.artist, b.artist))
}

async function reminderDue(): Promise<boolean> {
  const db = getDb()
  const [lastBackupAt, lastChangeAt, firstDataAt, songs] = await Promise.all([
    getMeta(db, 'lastBackupAt'),
    getMeta(db, 'lastChangeAt'),
    getMeta(db, 'firstDataAt'),
    db.songs.count(),
  ])
  if (songs === 0) return false
  const number = (value: number | string | null) => (typeof value === 'number' ? value : null)
  return backupDue({
    now: Date.now(),
    lastBackupAt: number(lastBackupAt),
    lastChangeAt: number(lastChangeAt),
    firstDataAt: number(firstDataAt),
  })
}

/** Tres canciones inventadas para probar la app sin pegar nada propio. */
async function addExamples(): Promise<void> {
  const { fixtures } = await import('../../../tests/fixtures')
  const db = getDb()
  const now = new Date().toISOString()
  for (const fixture of fixtures) {
    const parsed = parseChordPro(fixture.chordpro)
    await createSong(
      db,
      newSongDocument({
        title: parsed.title ?? fixture.name,
        artist: parsed.artist ?? '',
        content: parsed.content,
        now,
        author: 'import',
      }),
      now,
    )
  }
}

export function Repertoire() {
  const { data: songs, error } = useLive(() => listSongs(getDb()), [])
  const { data: due } = useLive(reminderDue, [])
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<Sort>('title')
  const [dismissed, setDismissed] = useState(false)
  const [busy, setBusy] = useState(false)

  const shown = useMemo(() => {
    if (!songs) return []
    return sortSongs(
      filterByQuery(songs, query, (song) => [song.title, song.artist]),
      sort,
    )
  }, [songs, query, sort])

  const r = t.repertoire
  const sortOptions: [Sort, string][] = [
    ['title', r.sortTitle],
    ['artist', r.sortArtist],
    ['recent', r.sortRecent],
  ]

  return (
    <div className={common.page}>
      <div className={common.row}>
        <h1 className={common.pageTitle}>{r.title}</h1>
        {songs && songs.length > 0 && <span className={styles.count}>{r.count(songs.length)}</span>}
        <a className={common.spacer} href={`#${paths.new}`}>
          <button className="primary">{r.add}</button>
        </a>
      </div>

      {due && !dismissed && (
        <div className={common.noticeWarn} role="status">
          <p style={{ margin: '0 0 10px' }}>{r.backupDue}</p>
          <div className={common.row}>
            <button className="primary" onClick={() => navigate(paths.settings)}>
              {r.backupNow}
            </button>
            <button onClick={() => setDismissed(true)}>{r.backupLater}</button>
          </div>
        </div>
      )}

      {error ? <p className={common.noticeError}>{r.loadError}</p> : null}

      {songs && songs.length === 0 && (
        <div className={styles.empty}>
          <h2>{r.emptyTitle}</h2>
          <p>{r.emptyText}</p>
          <div className={common.row} style={{ justifyContent: 'center' }}>
            <a href={`#${paths.new}`}>
              <button className="primary">{r.add}</button>
            </a>
            <button
              disabled={busy}
              onClick={() => {
                setBusy(true)
                addExamples().finally(() => setBusy(false))
              }}
            >
              {r.emptyExample}
            </button>
          </div>
        </div>
      )}

      {songs && songs.length > 0 && (
        <>
          <div className={styles.tools}>
            <input
              className={styles.search}
              type="search"
              aria-label={r.searchLabel}
              placeholder={r.searchPlaceholder}
              value={query}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              onChange={(event) => setQuery(event.target.value)}
            />
            <div className={styles.sort} role="group" aria-label={r.sort}>
              {sortOptions.map(([value, label]) => (
                <button
                  key={value}
                  aria-pressed={sort === value}
                  className={sort === value ? common.toggleOn : common.toggle}
                  onClick={() => setSort(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {shown.length === 0 ? (
            <p className={common.muted}>{r.noResults(query)}</p>
          ) : (
            <ul className={styles.list}>
              {shown.map((song) => (
                <li key={song.songId} className={styles.item}>
                  <a className={styles.open} href={`#${paths.play(song.arrangementId)}`}>
                    <p className={styles.title}>{song.title}</p>
                    {song.artist && <p className={styles.artist}>{song.artist}</p>}
                    <span className={styles.badges}>
                      {song.shapeKey && (
                        <span className={common.badge}>{r.badgeKey(song.shapeKey)}</span>
                      )}
                      {song.capo > 0 && (
                        <span className={common.badge}>{r.badgeCapo(song.capo)}</span>
                      )}
                      {song.bpm !== undefined && (
                        <span className={common.badge}>{r.badgeBpm(song.bpm)}</span>
                      )}
                      {song.status && <span className={common.badge}>{r.status[song.status]}</span>}
                      {song.needsReview && <span className={common.badgeWarn}>{r.review}</span>}
                    </span>
                  </a>
                  <a className={styles.edit} href={`#${paths.edit(song.arrangementId)}`}>
                    {r.edit}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  )
}
